import type { FetchError } from 'ofetch';
import type { NuxtApp } from '#app';
import type { ProblemDetails } from '~/types/ecommerce';

type ProblemDetailsFetchError = FetchError<ProblemDetails> & { data: ProblemDetails };

export const noop = () => {};

export const isNumeric = (num: unknown) =>
  (typeof num === 'number' || (typeof num === 'string' && num.trim() !== ''))
  && !isNaN(num as number);

export const getCachedPayload = (key: string, nuxtApp: NuxtApp) => nuxtApp.payload.data[key];

export const range = (length: number) => Array.from({ length }, (_value, index) => index);

/**
 * Extracts a user-friendly error message from a given `unknown` error value.
 *
 * This function is useful in global error handlers, catch blocks, or toast notifications
 * to present meaningful information when HTTP requests fail.
 *
 * It prioritizes error sources in the following order:
 * 1. A `FetchError` with a structured `data.message` field
 * 2. A generic `FetchError` with `status` and `statusText`
 * 3. A native JavaScript `Error` object
 * 4. Fallback: `"An unexpected error occurred."`
 *
 * @param error - The unknown error thrown from a `try/catch` block
 * @returns A string describing the error
 *
 * @example
 * try {
 *   await $fetch('/api/thing')
 * } catch (error) {
 *   const message = getErrorMessage(error)
 *   toast.error(message)
 * }
 */
export const getErrorMessage = (error: unknown): string => {
  // 1. ProblemDetails-style error
  if (isProblemDetailsError(error)) {
    const pd = error.data;

    // Prioritize field-level messages if available
    const errors = getProblemDetailsErrors(pd);

    if (errors.length) {
      return errors.join(' / ');
    }

    // Fallback to detail or title
    if (pd.detail && typeof pd.detail === 'string' && pd.detail.trim()) {
      return pd.detail.trim();
    }

    if (pd.title && typeof pd.title === 'string' && pd.title.trim()) {
      return pd.title.trim();
    }

    // Last fallback: HTTP status
    const status = pd.status ?? error.status;
    const title = pd.title || error.statusText || 'Unknown error';
    return `Request failed with status ${status} (${title})`;
  }

  // 2. Generic FetchError
  if (isFetchError(error)) {
    if (error.data
      && typeof error.data === 'object'
      && 'message' in error.data
      && typeof error.data.message === 'string'
    ) {
      return error.data.message;
    }

    return `Request failed with status ${error.status} (${error.statusText || 'Unknown error'})`;
  }

  // 3. Native JS Error
  if (error instanceof Error) {
    return error.message;
  }

  // 4. Unknown fallback
  return 'An unexpected error occurred.';
};

/**
 * Determines whether the provided value is a FetchError.
 *
 * A FetchError is typically thrown by HTTP clients like `ofetch` or `ky`
 * and contains basic HTTP response metadata such as `status` and `statusText`.
 *
 * @param error - The value to test (usually caught from a try/catch block)
 * @returns `true` if the error is a FetchError, otherwise `false`
 *
 * @example
 * try {
 *   await $fetch('/api/data');
 * } catch (error) {
 *   if (isFetchError(error)) {
 *     console.error(error.status, error.statusText);
 *   }
 * }
 */
export const isFetchError = (error: unknown): error is FetchError =>
  typeof error === 'object'
  && error !== null
  && 'status' in error
  && 'statusText' in error;

/**
 * Determines whether the provided value is a FetchError that contains
 * a Problem Details response body, typically following RFC 7807.
 *
 * This is useful for structured API error responses that include
 * machine-readable metadata like `type`, `title`, and `detail`.
 *
 * @param error - The value to test (usually caught from a try/catch block)
 * @returns `true` if the error is a FetchError with a ProblemDetails body, otherwise `false`
 *
 * @example
 * try {
 *   await $fetch('/api/secure-action');
 * } catch (error) {
 *   if (isProblemDetailsError(error)) {
 *     console.warn('API Error:', error.data.title);
 *   }
 * }
 */
export const isProblemDetailsError = (error: unknown): error is ProblemDetailsFetchError =>
  isFetchError(error)
  && !!error.data
  && typeof error.data === 'object'
  && 'type' in error.data
  && 'title' in error.data;

/**
 * Extracts all validation or field-level error messages from a ProblemDetails object.
 *
 * This is useful for APIs that follow RFC 7807 and include an `errors` dictionary
 * with field-specific messages (e.g., for form validation). Each entry in the
 * `errors` object may contain a single string or an array of strings.
 *
 * @param pd - A `ProblemDetails` object returned by the server, typically from a failed request.
 * @returns An array of all string messages found in `pd.errors`. If none are present, returns an empty array.
 *
 * @example
 * const pd = {
 *   title: "Validation Failed",
 *   errors: {
 *     email: ["Email is required.", "Email must be valid."],
 *     password: "Password is too short."
 *   }
 * };
 *
 * const messages = getProblemDetailsErrors(pd);
 * // → ["Email is required.", "Email must be valid.", "Password is too short."]
 */
export const getProblemDetailsErrors = (pd: ProblemDetails) => {
  const messages: string[] = [];

  if (pd.errors && typeof pd.errors === 'object') {
    for (const value of Object.values(pd.errors)) {
      const arr = Array.isArray(value) ? value : [String(value)];
      messages.push(...arr);
    }
  }

  return messages;
};

/**
 * Displays user-facing notifications for API errors that follow
 * RFC 7807 Problem Details, using Quasar's `Notify`.
 *
 * Priority of messages:
 * 1) `errors` dictionary (validation/field-level messages)
 * 2) `detail`
 * 3) `title`
 * 4) Fallback: HTTP status line
 *
 * Side effects:
 * - Emits one `Notify.create({ type: 'negative', message })` per message.
 *
 * @param error - A `FetchError<ProblemDetails>` thrown by your HTTP client
 * @returns void
 *
 * @example
 * try {
 *   await $fetch('/api/secure-action');
 * } catch (err) {
 *   if (isProblemDetailsError(err)) {
 *     handleProblemDetailsError(err); // shows notifications
 *   }
 * }
 */
export const handleProblemDetailsError = (error: FetchError<ProblemDetails>) => {
  const pd = error.data;
  const messages: string[] = [];

  // If the response body is missing or not a valid object, fallback immediately
  if (!pd || typeof pd !== 'object') {
    const fallback = `Request failed with status ${error.status} (${error.statusText || 'Unknown error'})`;
    Notify.create({ type: 'negative', message: fallback });
    return;
  }

  // 1) Field-level errors (RFC 7807 extension)
  const errors = getProblemDetailsErrors(pd);

  if (errors.length) {
    messages.push(...errors);
  }

  // 2) Detail message
  if (messages.length === 0 && typeof pd.detail === 'string' && pd.detail.trim()) {
    messages.push(pd.detail.trim());
  }

  // 3) Title message
  if (messages.length === 0 && typeof pd.title === 'string' && pd.title.trim()) {
    messages.push(pd.title.trim());
  }

  // 4) Final fallback to HTTP status info
  if (messages.length === 0) {
    const status = pd.status ?? error.status;
    const title = pd.title || error.statusText || 'Unknown error';
    messages.push(`Request failed with status ${status} (${title})`);
  }

  // Emit each message as a Quasar notification
  for (const message of messages) {
    Notify.create({ type: 'negative', message });
  }
};

/**
 * Handles any `unknown` FetchError error by delegating to the appropriate
 * helper in this module and showing user-facing notifications.
 *
 * Routing logic:
 * - If it's a Problem Details error (RFC 7807), call `handleProblemDetailsError`.
 * - Otherwise, build a message via `getErrorMessage` and notify once.
 *
 * Side effects:
 * - Emits Quasar `Notify` messages to the user.
 * - Logs the raw error to the console for diagnostics.
 *
 * @param error - The `unknown` error caught from a `try/catch` block
 * @returns void
 *
 * @example
 * try {
 *   await $fetch('/api/thing');
 * } catch (error) {
 *   handleFetchError(error); // shows notifications
 * }
 */
export const handleFetchError = (error: unknown): void => {
  // Always log the raw error for developer diagnostics
  // (safe no-op in production if console is filtered)
  console.error('[handleFetchError]', error);

  try {
    if (isProblemDetailsError(error)) {
      // Structured RFC 7807 response: show one or more messages
      handleProblemDetailsError(error);
      return;
    }

    // All other cases: derive a single user-friendly message
    Notify.create({ type: 'negative', message: getErrorMessage(error) });
  }
  catch {
    // Absolute last-resort fallback
    Notify.create({
      type: 'negative',
      message: 'An unexpected error occurred.',
    });
  }
};

/**
 * Produce a deterministic JSON string for a given value.
 *
 * Behavior mirrors the original JS implementation:
 * - Throws if the input is a number that is `NaN` or non-finite (`±Infinity`).
 * - For primitives and `null`, delegates to `JSON.stringify` (so `undefined` → `undefined`).
 * - If the value has a `toJSON()` function, that result is canonicalized instead.
 * - Arrays:
 *    - Elements that are `undefined` or of type `symbol` are serialized as `null`.
 *    - (Note) Sparse array "holes" are skipped by `Array.prototype.reduce` semantics in the original;
 *      this implementation intentionally preserves that behavior.
 * - Objects:
 *    - Own enumerable string keys are sorted lexicographically (by code point).
 *    - Properties whose value is `undefined` or a `symbol` are omitted.
 *
 * @param value Any JSON-serializable input.
 * @returns A canonical JSON string, or `undefined` for unsupported top-level cases
 *          (e.g., `undefined`, functions, symbols), matching `JSON.stringify`.
 * @throws Error when the top-level value is `NaN` or non-finite number.
 *
 * @example
 * canonicalize({ b: 2, a: 1 })           // -> '{"a":1,"b":2}'
 * canonicalize([1, undefined, 3])        // -> '[1,null,3]'
 * canonicalize(undefined)                // -> undefined
 * canonicalize(NaN)                      // -> throws Error('NaN is not allowed')
 */
export const canonicalize = (value: unknown): string | undefined => {
  // Guard against non-finite numbers (matches original behavior)
  if (typeof value === 'number') {
    if (Number.isNaN(value)) {
      throw new Error('NaN is not allowed');
    }

    if (!Number.isFinite(value)) {
      throw new Error('Infinity is not allowed');
    }
  }

  // Primitives & null → delegate to JSON.stringify (may return undefined)
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  // Respect custom serialization
  if ('toJSON' in value && typeof value.toJSON === 'function') {
    return canonicalize(value.toJSON());
  }

  // Arrays: undefined/symbol elements → null; join with commas
  if (Array.isArray(value)) {
    const items = (value as unknown[]).reduce((acc, current, idx) => {
      const comma = idx === 0 ? '' : ',';
      const normalized = current === undefined || typeof current === 'symbol' ? null : current;
      return `${acc}${comma}${canonicalize(normalized)}`;
    }, '');
    return `[${items}]`;
  }

  // Plain objects: sort keys; omit undefined/symbol values
  const obj = value as Record<string, unknown>;

  const body = Object.keys(obj)
    .sort()
    .reduce((acc, key) => {
      const v = obj[key];

      if (v === undefined || typeof v === 'symbol') {
        return acc;
      }

      const comma = acc.length === 0 ? '' : ',';
      return `${acc}${comma}${JSON.stringify(key)}:${canonicalize(v)}`;
    }, '');

  return `{${body}}`;
};
